import * as api from "@/api";
import styles from "@/components/search-suggestions.module.css";
import { globals } from "@/globals";
import useShortcuts from "@/hooks/use-shortcuts";
import * as signals from "@/signals";
import { BookmarkInfo } from "@/types";
import {
	clearSearchHistory,
	copyText,
	getShortcutTip,
	getTranslatorUrl,
	handleKeyboardClick,
	isFolder,
	openWebPage,
	search,
	sleep,
} from "@/utils";
import { useSignalEffect } from "@preact/signals";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { t } from "i18next";
import {
	JSX,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

interface SuggestionInfo {
	icon?: string;
	isDarkened?: boolean;
	shortcut?: string;
	text?: string;
	onClick: () => void | Promise<void>;
}

interface SearchSuggestionsProps {
	searchInputRef: RefObject<HTMLInputElement | null>;
	setSuggestedText: (newValue: string | null) => void;
}

function SearchSuggestions({
	searchInputRef,
	setSuggestedText,
}: SearchSuggestionsProps): JSX.Element {
	const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
	const [isSearchFocused, setIsSearchFocused] = useState(false);
	const [searchInputValue, setSearchInputValue] = useState<string>("");

	const region = signals.dynamicInfo.value.region;
	const isInChina = !region || region === "CN";

	const { data, isFetched } = useQuery({
		queryFn: () =>
			isInChina
				? api.getBaiduSearchSuggestions(searchInputValue)
				: api.getBingSearchSuggestions(searchInputValue),
		queryKey: ["searchSuggestions", searchInputValue],
		refetchOnMount: false,
		refetchOnWindowFocus: false,
	});
	const { data: shortcuts } = useShortcuts();

	const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
	const mathPattern = /^[+-]?\d+(\.\d+)?([+\-*/]\d+(\.\d+)?)+$/;
	const urlPattern =
		/([\w+]+:\/\/)?([\w\d-]+\.)*[\w-]+[.:]\w+([/?=&#.]?[\w-]+)*\/?/m;

	let actionSuggetion: SuggestionInfo | null = null;
	if (searchInputValue) {
		const searchInputValueNoSpaces = searchInputValue.replace(/\s/g, "");
		const searchInputValueLowered = searchInputValue.toLowerCase();
		let matchedShortcut: BookmarkInfo | undefined;
		if (shortcuts) {
			for (const shortcut of shortcuts.children) {
				if (isFolder(shortcut)) {
					for (const child of shortcut.children) {
						if (
							child.title
								.toLowerCase()
								.includes(searchInputValueLowered)
						) {
							matchedShortcut = child;
						}
					}
				} else if (
					shortcut.itemType === "bookmark" &&
					shortcut.title
						.toLowerCase()
						.includes(searchInputValueLowered)
				) {
					matchedShortcut = shortcut;
				}
			}
		}
		if (matchedShortcut) {
			actionSuggetion = {
				icon: "\ue67d",
				onClick: (): void => {
					if (!matchedShortcut?.url) {
						throw new Error("The shortcut has no URL.");
					}
					openWebPage(matchedShortcut.url);
				},
				text: t("openShortcut") + matchedShortcut.title,
			};
		} else if (
			globals.mathJs &&
			mathPattern.test(searchInputValueNoSpaces)
		) {
			const math = globals.mathJs;
			const calculationResult = math.number(
				math.format(math.evaluate(searchInputValueNoSpaces), {
					precision: 14,
				}),
			);
			actionSuggetion = {
				icon: "\ue759",
				onClick: (): void => {
					void copyText(
						searchInputValueNoSpaces + " = " + calculationResult,
					);
				},
				text: `${t("copyEquation")}${searchInputValueNoSpaces} = ${calculationResult}`,
			};
		} else if (urlPattern.test(searchInputValueNoSpaces)) {
			actionSuggetion = {
				icon: "\ue676",
				onClick: (): void => {
					openWebPage(searchInputValueNoSpaces);
				},
				text:
					t("visit") + urlPattern.exec(searchInputValueNoSpaces)?.[0],
			};
		} else {
			actionSuggetion = {
				icon: "\ue67f",
				onClick: (): void => {
					const translatorUrl = getTranslatorUrl(
						signals.selectedSearchEngine.value.id,
					);
					openWebPage(
						translatorUrl + encodeURIComponent(searchInputValue),
					);
				},
				text: t("translateColon") + searchInputValue,
			};
		}
		actionSuggetion.shortcut = getShortcutTip("Enter", {
			altKey: true,
		});
	}
	const hasHistory =
		!searchInputValue &&
		signals.generalSettings.value.isSearchHistoryEnabled &&
		signals.searchHistory.value.length > 0;
	const hasSuggestions =
		hasHistory || actionSuggetion || (data && data.length > 0);

	const closeSuggestions = useCallback(async (): Promise<void> => {
		signals.isClosingSearchSuggestions.value = true;
		await sleep(250);
		signals.isClosingSearchSuggestions.value = false;
		setSearchInputValue("");
	}, []);

	const suggestions = useMemo((): SuggestionInfo[] => {
		const suggestions: SuggestionInfo[] = [];
		if (actionSuggetion) {
			suggestions.push(actionSuggetion);
		}
		if (data) {
			suggestions.push(
				...data.map((item) => {
					return {
						onClick: (): void => {
							search(item);
						},
						text: item,
					};
				}),
			);
		}
		if (hasHistory) {
			suggestions.push(
				...signals.searchHistory.value.toReversed().map((item) => {
					return {
						onClick: (): void => {
							search(item);
						},
						text: item,
					};
				}),
				{
					icon: "\ueb95",
					isDarkened: true,
					onClick: async (): Promise<void> => {
						await closeSuggestions();
						clearSearchHistory();
					},
					text: t("clearHistory"),
				},
			);
		}
		return suggestions;
	}, [actionSuggetion, closeSuggestions, data, hasHistory]);

	useSignalEffect(() => {
		// Set the state in component to ensure the transition works
		setIsSearchFocused(signals.isSearchFocused.value);
	});

	useSignalEffect(() => {
		if (signals.searchInputValue.value || hasHistory) {
			setSearchInputValue(signals.searchInputValue.value);
		} else {
			void closeSuggestions();
		}
	});

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent): void => {
			if (!hasSuggestions) {
				return;
			}
			if (event.key === "ArrowDown" || event.key === "ArrowUp") {
				let newIndex: number;
				if (event.key === "ArrowDown") {
					newIndex =
						focusedIndex === null ||
						focusedIndex === suggestions.length - 1
							? 0
							: focusedIndex + 1;
				} else {
					newIndex =
						focusedIndex === null || focusedIndex === 0
							? suggestions.length - 1
							: focusedIndex - 1;
				}
				setFocusedIndex(newIndex);
				const isSearchTermSuggestion = !suggestions[newIndex].icon;
				setSuggestedText(
					isSearchTermSuggestion
						? suggestions[newIndex].text || null
						: null,
				);
			} else if (event.key !== "Enter" || focusedIndex === null) {
				searchInputRef.current?.focus();
			}
		};
		window.addEventListener("keydown", handleKeyDown);
		return (): void => {
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, [
		actionSuggetion,
		data,
		focusedIndex,
		hasSuggestions,
		searchInputRef,
		setSuggestedText,
		suggestions,
	]);

	useEffect(() => {
		const handleInputKeyDown = (event: KeyboardEvent): void => {
			if (event.altKey && event.key === "Enter") {
				void actionSuggetion?.onClick();
			}
		};

		const currentSearchInput = searchInputRef.current;
		if (!currentSearchInput) {
			return;
		}
		currentSearchInput.addEventListener("keydown", handleInputKeyDown);

		return (): void => {
			currentSearchInput.removeEventListener(
				"keydown",
				handleInputKeyDown,
			);
		};
	}, [actionSuggetion, searchInputRef]);

	useEffect(() => {
		setFocusedIndex(null);
	}, [searchInputValue]);

	useEffect(() => {
		if (focusedIndex !== null && itemRefs.current[focusedIndex]) {
			itemRefs.current[focusedIndex]?.focus();
		}
	}, [focusedIndex]);

	if (!hasSuggestions) {
		return <></>;
	}

	const elements = suggestions.map((item, index) => {
		const handleClick = (): void => {
			void item.onClick();
		};

		return (
			<div
				aria-selected={focusedIndex === index}
				className={clsx(
					styles["suggestion-item"],
					item.isDarkened && styles["darken"],
					(item.icon || item.shortcut) && styles["action-item"],
				)}
				id={`suggestion-${index}`}
				key={index}
				ref={(element) => {
					itemRefs.current[index] = element;
				}}
				role="option"
				tabIndex={-1}
				onClick={handleClick}
				onKeyDown={handleKeyboardClick(handleClick)}
			>
				{item.icon || item.shortcut ? (
					<>
						<i>{item.icon}</i>
						<span className={styles["suggestion-text"]}>
							{item.text}
						</span>
						{item.shortcut && (
							<span className={styles["shortcut-tip"]}>
								{item.shortcut}
							</span>
						)}
					</>
				) : (
					item.text
				)}
			</div>
		);
	});

	return (
		<div
			className={clsx(
				"z-index", // above quote
				isFetched && styles["fade-in-and-expand"],
				signals.isClosingSearchSuggestions.value &&
					styles["fade-out-and-collapse"],
				isSearchFocused && styles["search-focused"],
				styles["search-suggestions-container"],
			)}
			role="listbox"
			tabIndex={0}
			aria-activedescendant={
				focusedIndex !== null ? `suggestion-${focusedIndex}` : undefined
			}
		>
			{elements}
		</div>
	);
}

export default SearchSuggestions;
